import React, { useEffect, useState } from 'react';
import styles from './IncreaseReport.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import ModalIcon from '../../Icon/Icon';
import { changeIncreaseReportModalProps } from '../../../../../store/actions/modals';
import { getTranslation } from '../../../../../utils/translation';
import Input from '../../Input/Input';
import Title from '../../Title/Title';
import CloseButton from '../../CloseButton/CloseButton';
import NumericKeyboard from '../../../VirtualKeyboard/NumericKeyboard';
import API from '../../../../../utils/api';
import Feedback from '../Feedback/Feedback';
import { AGENT_SERVICE_ENDPOINT_URL } from '../../../../../constants/defaultValues';
import Button from '../../Button/Button';
import { showErrorNotification } from '../../../../../utils/render';
import { sprintf } from 'sprintf-js';

const IncreaseReport = () => {
    const dispatch = useDispatch();

    const modalProps = useSelector(state => state.modals.increaseReportModal);

    const [amount, setAmount] = useState(200);
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorResponse, setErrorResponse] = useState({ visible: false });
    const [successResponse, setSuccessResponse] = useState({ visible: false });

    const { isVisible } = modalProps;

    useEffect(() => {
        if (!isVisible) {
            setAmount('');
            setIsKeyboardVisible(false);
            setIsSubmitting(false);
            resetErrorResponse();
            resetSuccessResponse();
        }
    }, [isVisible]);

    const onOk = async () => {
        try {
            if (amount < 200) {
                showErrorNotification(
                    sprintf(getTranslation('increaseBalanceMinimumAmountValidation'), {
                        amount: 200,
                    })
                );
                return;
            }

            if (amount > 50000) {
                showErrorNotification(
                    sprintf(getTranslation('increaseBalanceMaximumAmountValidation'), {
                        amount: 50000,
                    })
                );
                return;
            }

            if (amount % 100 !== 0) {
                showErrorNotification(
                    sprintf(getTranslation('increaseBalanceDivisibleByValidation'), {
                        amount: 100,
                    })
                );
                return;
            }

            setIsSubmitting(true);

            const response = await API.put(`${AGENT_SERVICE_ENDPOINT_URL}/balance`, { amount });

            setSuccessResponse({
                visible: true,
                message: getTranslation('increaseReportSuccessMessage'),
            });
            setIsSubmitting(false);
        } catch (error) {
            setErrorResponse({
                visible: true,
                message:
                    error.response?.data?.info?.errors?.[0]?.reason ||
                    getTranslation('increaseReportErrorModalDefaultMessage'),
            });
            setIsSubmitting(false);
        }
    };

    const onClose = () => {
        dispatch(changeIncreaseReportModalProps({ isVisible: false }));
    };

    const onAmountChange = e => {
        setAmount(e.target.value);
    };

    const onKeyboardClick = () => {
        setIsKeyboardVisible(visible => !visible);
    };

    const resetErrorResponse = () => {
        setErrorResponse({ visible: false });
    };

    const resetSuccessResponse = () => {
        setSuccessResponse({ visible: false });
    };

    const hideModal = () => {
        dispatch(changeIncreaseReportModalProps({ isVisible: false }));
    };

    return (
        <>
            <CloseButton onClick={onClose} />
            {successResponse.visible ? (
                <Feedback
                    onClose={hideModal}
                    icon={'success'}
                    title={successResponse.message}
                    buttonText={getTranslation('increaseReportSuccessModalOkButton')}
                />
            ) : null}
            {errorResponse.visible ? (
                <Feedback
                    onClose={resetErrorResponse}
                    title={errorResponse.message}
                    buttonText={getTranslation('increaseReportErrorModalGoBackButton')}
                />
            ) : null}
            {!(successResponse.visible || errorResponse.visible) && (
                <>
                    {isKeyboardVisible ? (
                        <NumericKeyboard
                            setInputValue={value => setAmount(value)}
                            close={onKeyboardClick}
                            style={{ bottom: '0', right: '-170px' }}
                        />
                    ) : null}
                    <ModalIcon iconKey='credit' />
                    <Title>{getTranslation('othersSidebarCreditLimitIncreaseReport')}</Title>
                    <div className={styles.inputContainer}>
                        <Input
                            inputType={'number'}
                            placeholder={getTranslation('increaseReportRequestAmountPlaceholder')}
                            marginTop='40px'
                            value={amount}
                            onChange={onAmountChange}
                            showVirtualKeyboard={onKeyboardClick}
                        />
                    </div>
                    <div className={styles.actionsContainer}>
                        <Button
                            type='submit'
                            disabled={!amount || isSubmitting}
                            onClick={onOk}
                            width='183px'
                            marginRight='15px'
                            title={getTranslation('increaseReportIncreaseButton')}
                        />
                        <Button
                            secondary
                            onClick={onClose}
                            width='102px'
                            title={getTranslation('common.actions.cancel')}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default IncreaseReport;
